// File: src/components/admin/plans/PlanPreviewCard.js
// live preview of what a logged-out visitor sees on the
// marketing pricing page for the plan currently being edited.
//
// Intentionally self-contained — we *don't* import the customer FE's
// PricingPage component because that lives in a different repo (defo-ads).
// The customer FE will render the same shape from /api/plans/public; this
// preview matches that layout closely so the admin can sanity-check copy
// without leaving the page.

import React from 'react';
import { Paper, Box, Typography, Chip, Button, Stack } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const BADGE_LABELS = {
    POPULAR: 'Most popular',
    BEST_VALUE: 'Best value',
    NEW: 'New',
};

function symbolFor(currency) {
    const c = String(currency || '').toUpperCase();
    if (c === 'USD') return '$';
    if (c === 'EUR') return '€';
    if (c === 'GBP') return '£';
    return `${c} `;
}

// i18n: when `locale` is supplied (and is not 'en'), the preview
// reads from draft.i18n[locale] for displayName/tagline/featureBullets and
// falls back to the canonical English fields when an override is missing.
// Mirrors backend applyLocaleToPlan so what the admin sees == what the
// /api/plans/public?lang= caller will get.
function pickLocalised(draft, locale) {
    if (!locale || locale === 'en') {
        return {
            displayName: draft.displayName || '',
            tagline: draft.tagline || '',
            featureBullets: Array.isArray(draft.featureBullets) ? draft.featureBullets : [],
        };
    }
    const override = draft.i18n?.[locale];
    if (!override) {
        return {
            displayName: draft.displayName || '',
            tagline: draft.tagline || '',
            featureBullets: Array.isArray(draft.featureBullets) ? draft.featureBullets : [],
        };
    }
    return {
        displayName: override.displayName || draft.displayName || '',
        tagline: override.tagline || draft.tagline || '',
        featureBullets: Array.isArray(override.featureBullets) && override.featureBullets.length > 0
            ? override.featureBullets
            : (Array.isArray(draft.featureBullets) ? draft.featureBullets : []),
    };
}

export default function PlanPreviewCard({ draft, planId, locale = 'en' }) {
    const localised = pickLocalised(draft, locale);
    const symbol = symbolFor(draft.currency);
    const priceUnits = Math.floor(Number(draft.price ?? 0) / 100);
    const priceCents = Math.abs(Number(draft.price ?? 0)) % 100;
    const interval = draft.interval || 'month';
    const isHighlighted = !!draft.badge;
    const badgeLabel = BADGE_LABELS[draft.badge];
    const features = localised.featureBullets;

    return (
        <Paper
            data-testid={`plan-preview-${planId}`}
            variant="outlined"
            sx={{
                p: 3, position: 'relative',
                borderColor: isHighlighted ? '#7b2ff2' : 'divider',
                borderWidth: isHighlighted ? 2 : 1,
                boxShadow: isHighlighted ? 4 : 0,
            }}
        >
            {badgeLabel && (
                <Chip
                    label={badgeLabel}
                    size="small"
                    data-testid={`plan-preview-badge-${planId}`}
                    sx={{
                        position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                        bgcolor: '#7b2ff2', color: '#fff', fontWeight: 600,
                    }}
                />
            )}

            <Typography
                variant="h5"
                sx={{ fontWeight: 700, mb: 0.5 }}
                data-testid={`plan-preview-name-${planId}`}
            >
                {localised.displayName || draft.name || planId}
            </Typography>

            {localised.tagline && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                    data-testid={`plan-preview-tagline-${planId}`}
                >
                    {localised.tagline}
                </Typography>
            )}

            <Box sx={{ mb: 2 }}>
                <Typography component="span" sx={{ fontWeight: 800, fontSize: '2.4rem' }}>
                    {symbol}{priceUnits}
                </Typography>
                {priceCents > 0 && (
                    <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.2rem' }}>
                        .{String(priceCents).padStart(2, '0')}
                    </Typography>
                )}
                <Typography component="span" color="text.secondary">/{interval}</Typography>
            </Box>

            <Button
                variant={isHighlighted ? 'contained' : 'outlined'}
                fullWidth
                sx={{
                    textTransform: 'none', mb: 2.5,
                    ...(isHighlighted
                        ? { bgcolor: '#7b2ff2', '&:hover': { bgcolor: '#5a1db8' } }
                        : { borderColor: '#7b2ff2', color: '#7b2ff2' }),
                }}
            >
                Start free trial
            </Button>

            <Stack spacing={0.5}>
                {features.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                        (no feature bullets — add them in the marketing fields above)
                    </Typography>
                )}
                {features.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.6, borderBottom: 1, borderColor: 'divider' }}>
                        <CheckIcon sx={{ fontSize: 16, color: '#7b2ff2', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">{f}</Typography>
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
}
